/**
 * /admin/dashboard — CRM KPI dashboard.
 *
 * Renders the payload from GET /api/admin/crm/dashboard as:
 *  - top KPI cards (total leads / verified / disbursed / open follow-ups)
 *  - conversion funnel bars (new → contacted → qualified → approved → disbursed)
 *  - status breakdown pie-ish list
 *  - aging buckets (0-2d / 3-7d / 8-30d / 30d+)
 *  - per-staff performance table (top 20 by lead volume)
 *  - 30-day new-lead trend spark
 *  - follow-up load (open / overdue / today)
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  ShieldCheck,
  IndianRupee,
  Hourglass,
  Layers,
  Activity,
  RefreshCw,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Download,
} from "lucide-react";
import { CRM, tokenStorage, currentUser, adminApi } from "@/lib/api";
import Logo from "@/components/Logo";

const STAGE_META = {
  new:       { label: "New",       color: "#5EA9FF" },
  contacted: { label: "Contacted", color: "#F9C74F" },
  qualified: { label: "Qualified", color: "#6EE7B7" },
  approved:  { label: "Approved",  color: "#F0D788" },
  disbursed: { label: "Disbursed", color: "#D4AF37" },
  pending:   { label: "Pending",   color: "#F59E0B" },
  rejected:  { label: "Rejected",  color: "#EF4444" },
  closed:    { label: "Closed",    color: "#A3A3A3" },
};

export default function CrmDashboard() {
  const nav = useNavigate();
  const me = currentUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tokenStorage.get()) { nav("/admin", { replace: true }); return; }
    if (me && !["admin", "manager"].includes(me.role)) {
      nav("/admin/leads", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true); setError("");
    try {
      const d = await CRM.dashboard();
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin", { replace: true });
      setError(e?.response?.data?.detail || "Failed to load dashboard.");
    } finally { setLoading(false); }
  }

  if (loading) return <Shell><div className="py-32 text-center text-white/60">Loading dashboard…</div></Shell>;
  if (error) return <Shell><div className="py-16 text-center text-red-200"><AlertTriangle size={18} className="inline mr-2" />{error}</div></Shell>;
  if (!data) return null;

  const funnelMax = Math.max(...data.funnel.map((f) => f.count), 1);

  return (
    <Shell onRefresh={refresh}>
      {/* ----- Top KPI cards ----- */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-leads" label="Total leads" value={data.total_leads} icon={Users} tone="gold" />
        <KpiCard
          testId="kpi-verified"
          label="Verified"
          value={countBy(data.by_verification, "verified")}
          sub={`${pct(countBy(data.by_verification, "verified"), data.total_leads)}% of total`}
          icon={ShieldCheck}
          tone="emerald"
        />
        <KpiCard
          testId="kpi-disbursed"
          label="Disbursed"
          value={countBy(data.by_status, "disbursed")}
          sub={`Conversion ${data.conversion_rate}%`}
          icon={IndianRupee}
          tone="gold"
        />
        <KpiCard
          testId="kpi-followups-open"
          label="Open follow-ups"
          value={data.followups.open}
          sub={data.followups.overdue ? `${data.followups.overdue} overdue` : "on track"}
          icon={Hourglass}
          tone={data.followups.overdue ? "red" : "blue"}
        />
      </section>

      {/* ----- Funnel + status split ----- */}
      <section className="mt-8 grid md:grid-cols-2 gap-6">
        <div data-testid="dashboard-funnel" className="rounded-3xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6 md:p-7">
          <div className="flex items-center gap-2.5 text-[#F0D788]">
            <TrendingUp size={16} />
            <span className="text-xs tracking-[0.22em] uppercase">Conversion Funnel</span>
          </div>
          <h2 className="mt-2 font-display text-2xl">New → Disbursed</h2>
          <div className="mt-6 space-y-3">
            {data.funnel.map((f) => (
              <div key={f.stage}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/90">{STAGE_META[f.stage]?.label || f.stage}</span>
                  <span className="text-white font-semibold">{f.count}</span>
                </div>
                <div className="mt-1.5 h-3 rounded-full bg-black/40 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(2, (f.count / funnelMax) * 100)}%`,
                      background: STAGE_META[f.stage]?.color || "#D4AF37",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-white/70">
            Overall conversion: <span className="text-[#F0D788] font-semibold">{data.conversion_rate}%</span>
            {" "}from first contact to disbursement.
          </p>
        </div>

        <div data-testid="dashboard-status-split" className="rounded-3xl border border-white/10 bg-[#0A1F44] p-6 md:p-7">
          <div className="flex items-center gap-2.5 text-[#F0D788]">
            <Layers size={16} />
            <span className="text-xs tracking-[0.22em] uppercase">Status breakdown</span>
          </div>
          <h2 className="mt-2 font-display text-2xl">Where every lead lives</h2>
          <div className="mt-6 space-y-2.5">
            {data.by_status
              .slice()
              .sort((a, b) => b.n - a.n)
              .map((s) => {
                const meta = STAGE_META[s._id] || { label: s._id, color: "#888" };
                const pctVal = Math.round((s.n / Math.max(1, data.total_leads)) * 100);
                return (
                  <div key={s._id} className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                    <div className="flex-1 text-white/90">{meta.label}</div>
                    <div className="w-16 text-right text-white/85 font-mono">{s.n}</div>
                    <div className="w-12 text-right text-white/50 text-xs">{pctVal}%</div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* ----- Aging + Follow-ups + Trend ----- */}
      <section className="mt-8 grid md:grid-cols-3 gap-6">
        <div data-testid="dashboard-aging" className="rounded-3xl border border-white/10 bg-[#0A1F44] p-6">
          <div className="flex items-center gap-2.5 text-[#F0D788]">
            <Calendar size={14} />
            <span className="text-[11px] tracking-[0.22em] uppercase">Aging</span>
          </div>
          <h3 className="mt-2 font-display text-lg">Open lead age</h3>
          <div className="mt-4 space-y-2 text-sm">
            {Object.entries(data.age_buckets).map(([bucket, n]) => (
              <div key={bucket} className="flex items-center justify-between rounded-lg bg-black/25 border border-white/[0.06] px-3 py-2">
                <span className="text-white/85">{bucket}</span>
                <span className={`font-mono ${bucket === "30d+" && n > 0 ? "text-red-300" : "text-[#F0D788]"}`}>{n}</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="dashboard-followups" className="rounded-3xl border border-white/10 bg-[#0A1F44] p-6">
          <div className="flex items-center gap-2.5 text-[#F0D788]">
            <Hourglass size={14} />
            <span className="text-[11px] tracking-[0.22em] uppercase">Follow-ups</span>
          </div>
          <h3 className="mt-2 font-display text-lg">Task load</h3>
          <div className="mt-4 space-y-2 text-sm">
            <FUpRow label="Open" n={data.followups.open} />
            <FUpRow label="Due today" n={data.followups.due_today} tone={data.followups.due_today ? "amber" : "muted"} />
            <FUpRow label="Overdue" n={data.followups.overdue} tone={data.followups.overdue ? "red" : "muted"} />
          </div>
        </div>

        <div data-testid="dashboard-trend" className="rounded-3xl border border-white/10 bg-[#0A1F44] p-6">
          <div className="flex items-center gap-2.5 text-[#F0D788]">
            <Activity size={14} />
            <span className="text-[11px] tracking-[0.22em] uppercase">30-day inflow</span>
          </div>
          <h3 className="mt-2 font-display text-lg">New leads / day</h3>
          <Sparkline data={data.trend} />
        </div>
      </section>

      {/* ----- Staff performance ----- */}
      <section className="mt-8">
        <div data-testid="dashboard-staff" className="rounded-3xl border border-white/10 bg-[#0A1F44] overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#F0D788]">Team</div>
              <div className="font-display text-xl">Per-staff performance</div>
            </div>
            <span className="text-xs text-white/60">By total assigned leads (top 20)</span>
          </div>
          {(data.staff_performance || []).length === 0 && (
            <div className="py-14 text-center text-white/60 text-sm">
              No leads have been assigned to staff yet. Assign leads from the Team & Access page or Kanban.
            </div>
          )}
          {(data.staff_performance || []).length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/70 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-6 py-3">Staff</th>
                  <th className="text-right px-4 py-3">Assigned</th>
                  <th className="text-right px-4 py-3">Verified</th>
                  <th className="text-right px-4 py-3">Approved</th>
                  <th className="text-right px-4 py-3">Disbursed</th>
                  <th className="text-right px-6 py-3">Closed</th>
                </tr>
              </thead>
              <tbody>
                {data.staff_performance.map((s, i) => (
                  <tr key={s._id || i} className="border-t border-white/[0.06]">
                    <td className="px-6 py-3 text-white">{s._id || "—"}</td>
                    <td className="text-right px-4 text-[#F0D788] font-mono">{s.total}</td>
                    <td className="text-right px-4 text-emerald-300 font-mono">{s.verified}</td>
                    <td className="text-right px-4 text-white/80 font-mono">{s.approved}</td>
                    <td className="text-right px-4 text-[#F0D788] font-mono">{s.disbursed}</td>
                    <td className="text-right px-6 text-white/60 font-mono">{s.closed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children, onRefresh }) {
  const nav = useNavigate();
  return (
    <div data-testid="crm-dashboard-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="dashboard-back"
            onClick={() => nav("/admin/leads")}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> Back to Admin
          </button>
          <div className="flex items-center gap-3">
            <Logo size={40} testId="dashboard-logo" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">CRM Dashboard</div>
          </div>
          {onRefresh ? (
            <button
              data-testid="dashboard-refresh"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs text-white/85 hover:bg-white/[0.06]"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          ) : <div />}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Sales Command Center</h1>
            <p className="mt-2 text-white/70 text-sm max-w-2xl">
              Live pulse of leads, staff performance and follow-up load — everything CRM in one screen.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="dashboard-download-pdf"
              onClick={async () => {
                // Fetch as authenticated blob then trigger download — Bearer header + cookie both work.
                try {
                  const resp = await adminApi.get("/admin/crm/dashboard.pdf", { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `sahyog-crm-snapshot-${new Date().toISOString().slice(0,10)}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  alert("PDF download failed. Please retry.");
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 text-[#F0D788] text-sm hover:bg-[#D4AF37]/25"
            >
              <Download size={13} /> Weekly PDF
            </button>
            <Link to="/admin/pipeline" data-testid="dashboard-goto-pipeline" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#F0D788] text-sm hover:bg-[#D4AF37]/20">
              <Layers size={13} /> Open Kanban
            </Link>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-white/85 text-sm hover:bg-white/[0.09]">
              Leads table
            </Link>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}

function KpiCard({ testId, label, value, sub, icon: Icon, tone = "gold" }) {
  const toneMap = {
    gold:    "border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] to-[#04102B] text-[#F0D788]",
    emerald: "border-emerald-500/25 bg-gradient-to-br from-[#0A1F44] to-emerald-950/40 text-emerald-200",
    blue:    "border-blue-400/25 bg-gradient-to-br from-[#0A1F44] to-blue-950/40 text-blue-200",
    red:     "border-red-500/25 bg-gradient-to-br from-[#0A1F44] to-red-950/40 text-red-200",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-3xl border p-5 md:p-6 ${toneMap}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/70">{label}</div>
        <Icon size={16} className="opacity-85" />
      </div>
      <div className="mt-3 font-display text-3xl md:text-4xl text-white">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-white/75">{sub}</div>}
    </div>
  );
}

function FUpRow({ label, n, tone = "muted" }) {
  const cls = {
    muted: "text-white/85",
    amber: "text-amber-300",
    red:   "text-red-300",
  }[tone];
  return (
    <div className="flex items-center justify-between rounded-lg bg-black/25 border border-white/[0.06] px-3 py-2">
      <span className="text-white/85">{label}</span>
      <span className={`font-mono ${cls}`}>{n}</span>
    </div>
  );
}

function Sparkline({ data }) {
  if (!data || data.length === 0) {
    return <div className="mt-4 text-white/50 text-xs">No leads in the last 30 days.</div>;
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  const w = 100 / Math.max(data.length, 1);
  return (
    <div className="mt-5">
      <div className="flex items-end gap-[2px] h-28">
        {data.map((d) => {
          const h = Math.max(4, (d.count / max) * 100);
          return (
            <div
              key={d.day}
              title={`${d.day}: ${d.count}`}
              className="rounded-t bg-[#D4AF37]/60 hover:bg-[#D4AF37]"
              style={{ width: `${w}%`, height: `${h}%` }}
            />
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-white/55">
        <span>{data[0]?.day}</span>
        <span>Peak: {Math.max(...data.map((d) => d.count))}/day</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function countBy(rows, id) {
  const r = (rows || []).find((x) => x._id === id);
  return r ? r.n : 0;
}
function pct(part, total) {
  if (!total) return 0;
  return Math.round((100 * part) / total);
}
